import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, X, Send, Loader2, FileText, CheckCircle2, KeyRound, Globe, PenLine, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PlatformLayout } from "./PlatformLayout";
import { AnimatedTitle } from "./AnimatedTitle";
import { PortalBanner } from "./PortalBanner";
import { submitProperty, submitPropertyFromUrl, uploadSubmissionFile, PROPOSER_ONLINE_IMG, PROPOSER_OFFLINE_IMG, API } from "./api";
import { authApi, useAuth } from "./authApi";
import { CityAutocomplete } from "./CityAutocomplete";
import GenerationProgress from "./GenerationProgress";

const DRAFT_KEY = "om_seller_draft";
async function createSellerListing(payload) {
  const res = await fetch(`${API}/public/seller-listing`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
  });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Génération impossible");
  return d;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const abs = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u || ""}`);

const TYPES = ["Appartement", "Maison", "Terrain", "Immeuble", "Autre"];
const STAGING_STYLE_CHOICES = [
  { key: "contemporain", label: "Contemporain" },
  { key: "scandinave", label: "Scandinave" },
  { key: "minimaliste", label: "Minimaliste" },
  { key: "luxe", label: "Luxe / Prestige" },
  { key: "classique", label: "Classique français" },
  { key: "industriel", label: "Industriel" },
  { key: "mediterraneen", label: "Méditerranéen" },
  { key: "japandi", label: "Japandi" },
  { key: "cosy", label: "Cosy" },
  { key: "boheme_chic", label: "Bohème chic" },
];
const empty = {
  owner_nom: "", owner_prenom: "", owner_telephone: "", owner_email: "",
  type: "Maison", address: "", city: "", postal_code: "", department: "", region: "", surface: "", land: "",
  rooms: "", bedrooms: "", price: "", description: "", travaux: "", disponibilite: "",
  images: [], documents: [], consent: false,
};

export default function ProposerBien() {
  const nav = useNavigate();
  const { user, setUser } = useAuth();
  const [f, setF] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [mode, setMode] = useState(null); // null (choice) | "online" | "offline"
  const [on, setOn] = useState({ owner_nom: "", owner_prenom: "", owner_telephone: "", owner_email: "", url: "" });
  const [pending, setPending] = useState(null); // payload en attente de création de compte
  const [acc, setAcc] = useState({ email: "", password: "", name: "" });
  const [generating, setGenerating] = useState(false);
  const [genJobId, setGenJobId] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [stagingStyles, setStagingStyles] = useState(["contemporain", "scandinave"]);
  const toggleStyle = (k) => setStagingStyles((prev) => {
    if (prev.includes(k)) return prev.filter((x) => x !== k);
    if (prev.length >= 2) return [prev[1], k];
    return [...prev, k];
  });
  useEffect(() => { fetch(`${API}/credits/config`).then((r) => r.json()).then(setPricing).catch(() => {}); }, []);
  const photoRef = useRef();
  const docRef = useRef();

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setOnline = (k, v) => setOn((p) => ({ ...p, [k]: v }));

  // Reprise du parcours après connexion Google
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("resume") !== "1") return;
    let draft = null;
    try { draft = sessionStorage.getItem(DRAFT_KEY); } catch (_) {}
    if (!draft) return;
    try { sessionStorage.removeItem(DRAFT_KEY); } catch (_) {}
    let payload = null;
    try { payload = JSON.parse(draft); } catch (_) { return; }
    if (payload?.mode === "online") {
      startSellerJob(payload).catch(() => {});
    } else {
      setGenerating(true);
      createSellerListing(payload)
        .then((d) => nav(d?.slug ? `/mon-compte?wizard=${encodeURIComponent(d.slug)}` : "/mon-compte?welcome=1"))
        .catch((e) => { setErr(e.message); setGenerating(false); });
    }
  }, []); // eslint-disable-line

  // Lance l'import vendeur en tâche de fond et affiche la barre de progression temps réel.
  const startSellerJob = async (payload) => {
    setErr("");
    const r = await authApi.sellerListingStart(payload);
    setPending(null);
    setGenJobId(r.job_id);
  };

  const runGeneration = async (payload) => {
    setGenerating(true); setErr("");
    try {
      const d = await createSellerListing(payload);
      nav(d?.slug ? `/mon-compte?wizard=${encodeURIComponent(d.slug)}` : "/mon-compte?welcome=1");
    } catch (e) { setErr(e.message); setGenerating(false); }
  };

  const submitAccount = async (e) => {
    e && e.preventDefault();
    setErr("");
    if (!acc.email || (acc.password || "").length < 6) { setErr("E-mail et mot de passe (6 caractères min.) requis."); return; }
    setSending(true);
    try {
      if (!user) {
        const u = await authApi.registerUser({ email: acc.email, password: acc.password, name: acc.name || (pending?.property?.owner_prenom || "") });
        setUser && setUser(u);
      }
      if (pending?.mode === "online") {
        await startSellerJob(pending);
        setSending(false);
      } else {
        await runGeneration(pending);
      }
    } catch (ex) { setErr(ex.message || "Création impossible"); setSending(false); }
  };

  const accountGoogle = () => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(pending));
      sessionStorage.setItem("om_post_login", "/proposer?resume=1");
    } catch (_) {}
    const redirectUrl = window.location.origin + "/proposer?resume=1";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitOnline = async (e) => {
    e.preventDefault();
    setErr("");
    if (!on.url.trim().startsWith("http")) { setErr("Merci de saisir une URL valide (https://…)."); return; }
    const payload = { mode: "online", url: on.url.trim(), staging_styles: stagingStyles, property: { owner_nom: on.owner_nom, owner_prenom: on.owner_prenom, owner_telephone: on.owner_telephone, owner_email: on.owner_email } };
    if (user) {
      setSending(true);
      try { await startSellerJob(payload); } catch (ex) { setErr(ex.message || "Génération impossible"); }
      setSending(false);
    } else { setAcc((a) => ({ ...a, email: on.owner_email || "", name: on.owner_prenom || "" })); setPending(payload); }
  };

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true); setErr("");
    try {
      const urls = [];
      for (const file of files) { const r = await uploadSubmissionFile(file); urls.push(abs(r.url)); }
      set("images", [...f.images, ...urls]);
    } catch (ex) { setErr(ex.message); }
    setUploading(false);
  };

  const uploadDocs = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true); setErr("");
    try {
      const docs = [];
      for (const file of files) { const r = await uploadSubmissionFile(file); docs.push({ url: abs(r.url), name: r.name }); }
      set("documents", [...f.documents, ...docs]);
    } catch (ex) { setErr(ex.message); }
    setUploading(false);
  };

  const submit = (e) => {
    e.preventDefault();
    setErr("");
    if (!f.consent) { setErr("Merci d'accepter la politique de confidentialité (RGPD)."); return; }
    const payload = { mode: "manual", staging_styles: stagingStyles, property: { ...f } };
    if (user) { runGeneration(payload); }
    else { setAcc((a) => ({ ...a, email: f.owner_email || "", name: f.owner_prenom || "" })); setPending(payload); }
  };

  const inp = "w-full bg-white border border-zinc-300 px-4 py-3 text-sm text-[#050505] placeholder-zinc-400 focus:outline-none focus:border-[#C19A6B] transition-colors";
  const lbl = "block text-xs uppercase tracking-wider text-zinc-500 mb-1.5";
  const groupTitle = "calli text-lg text-[#C19A6B]";

  const stagingPickerEl = (
    <div className="border border-zinc-200 bg-white p-4 space-y-2" data-testid="staging-style-picker">
      <p className="text-xs uppercase tracking-wider text-[#C19A6B] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> 2 mises en scène IA offertes — choisissez vos 2 styles</p>
      <p className="text-[11px] text-zinc-500">Appliquées à la photo principale de votre bien. Par défaut : Contemporain + Scandinave.</p>
      <div className="flex flex-wrap gap-2">
        {STAGING_STYLE_CHOICES.map((s) => {
          const active = stagingStyles.includes(s.key);
          return (
            <button type="button" key={s.key} onClick={() => toggleStyle(s.key)} data-testid={`staging-style-${s.key}`}
              className={`text-xs px-3 py-1.5 border transition-colors ${active ? "border-[#C19A6B] bg-[#C19A6B]/10 text-[#8a6a3f] font-medium" : "border-zinc-300 text-zinc-600 hover:border-[#C19A6B]/60"}`}>
              {s.label}{active ? ` · ${stagingStyles.indexOf(s.key) + 1}` : ""}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-400" data-testid="staging-style-count">{stagingStyles.length}/2 sélectionnés</p>
    </div>
  );

  const Overlays = () => (
    <>
      <AnimatePresence>
        {pending && !generating && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setPending(null)} data-testid="seller-account-modal">
            <motion.div initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-md rounded-[5px] border border-white/15 bg-white/[0.07] backdrop-blur-md shadow-2xl text-white p-6 sm:p-7 text-center"
              onClick={(e) => e.stopPropagation()}>
              <div aria-hidden="true" className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#C19A6B]/20 blur-3xl pointer-events-none" />
              <button onClick={() => setPending(null)} data-testid="seller-account-close" className="absolute top-4 right-4 text-zinc-400 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
              <p className="calli text-3xl text-[#C19A6B] leading-none">Dernière étape</p>
              <p className="text-sm text-zinc-300 mt-2.5 leading-relaxed">Créez votre compte en un instant pour <strong className="font-semibold text-white">accéder à votre espace</strong> et lancer la <strong className="font-semibold text-white">génération IA</strong> de votre annonce, en prévisualisation privée.</p>
              <form onSubmit={submitAccount} className="mt-5 space-y-3 text-left">
                <input value={acc.name} onChange={(e) => setAcc((a) => ({ ...a, name: e.target.value }))} placeholder="Prénom" data-testid="seller-acc-name" className="w-full bg-white/[0.04] border border-white/12 px-4 py-2.5 text-sm text-white placeholder-zinc-300 outline-none focus:border-[#C19A6B] rounded-[5px]" />
                <input type="email" value={acc.email} onChange={(e) => setAcc((a) => ({ ...a, email: e.target.value }))} placeholder="E-mail *" data-testid="seller-acc-email" className="w-full bg-white/[0.04] border border-white/12 px-4 py-2.5 text-sm text-white placeholder-zinc-300 outline-none focus:border-[#C19A6B] rounded-[5px]" />
                <input type="password" value={acc.password} onChange={(e) => setAcc((a) => ({ ...a, password: e.target.value }))} placeholder="Mot de passe * (6 car. min.)" data-testid="seller-acc-password" className="w-full bg-white/[0.04] border border-white/12 px-4 py-2.5 text-sm text-white placeholder-zinc-300 outline-none focus:border-[#C19A6B] rounded-[5px]" />
                {err && <p className="text-sm text-red-400" data-testid="seller-acc-error">{err}</p>}
                <button type="submit" disabled={sending} data-testid="seller-acc-submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-semibold uppercase tracking-wider text-sm py-3 rounded-[5px] transition-colors">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Créer & générer mon annonce</>}
                </button>
              </form>
              <div className="flex items-center gap-3 py-3"><div className="flex-1 h-px bg-white/10" /><span className="text-[10px] uppercase tracking-wider text-zinc-500">ou</span><div className="flex-1 h-px bg-white/10" /></div>
              <button onClick={accountGoogle} data-testid="seller-acc-google" className="w-full flex items-center justify-center gap-2 border border-white/15 hover:border-[#C19A6B] text-white text-sm py-3 rounded-[5px] transition-colors">
                <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.5 5C9.6 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.3 35.9 44 30.5 44 24c0-1.3-.1-2.3-.4-3.5z" /></svg>
                Continuer avec Google
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {generating && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[130] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" data-testid="seller-generating">
            <div className="text-center text-white max-w-md">
              <Loader2 className="w-12 h-12 text-[#C19A6B] mx-auto animate-spin" />
              <p className="calli text-3xl text-[#C19A6B] mt-5">Génération de votre annonce…</p>
              <p className="text-sm text-zinc-300 mt-2.5 leading-relaxed">Notre IA rédige votre annonce et prépare votre espace personnel. Cela prend quelques secondes.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (genJobId) {
    return (
      <PlatformLayout solidHeader searchDock={false}>
        <div className="bg-zinc-100 min-h-screen" />
        <GenerationProgress
          jobId={genJobId}
          onView={(slug) => { setGenJobId(null); nav(slug ? `/bien/${slug}` : "/mon-compte"); }}
          onEdit={(slug) => { setGenJobId(null); nav(slug ? `/mon-compte?wizard=${encodeURIComponent(slug)}` : "/mon-compte"); }}
          onClose={() => { setGenJobId(null); nav("/mon-compte"); }}
        />
      </PlatformLayout>
    );
  }

  if (done) {
    return (
      <PlatformLayout solidHeader searchDock={false}>
        <div className="bg-zinc-100 text-[#050505] min-h-screen">
        <section className="pt-40 pb-32 max-w-2xl mx-auto px-6 text-center space-y-6">
          <CheckCircle2 className="w-16 h-16 text-[#C19A6B] mx-auto" data-testid="proposer-success" />
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight">Merci, votre bien a bien été soumis !</h1>
          <p className="text-zinc-600 font-light">Vincent Marette va analyser votre proposition et vous rappellera rapidement pour convenir d'un rendez-vous. Votre bien reste totalement confidentiel jusqu'à sa validation.</p>
          <a href="/" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-8 py-4 transition-colors">Retour à l'accueil</a>
        </section>
        </div>
      </PlatformLayout>
    );
  }

  // Écran de choix : image plein écran + popup semi-transparent
  if (mode === null) {
    const VISITE_IMG = "https://static.prod-images.emergentagent.com/jobs/8e57cf35-f4fb-40ce-b502-ff37f5224c96/images/7b0c3f98d1129ed7c6f6868587701897274d88a330a79126152120b23dbb7058.jpeg";
    return (
      <PlatformLayout>
        <div className="relative min-h-screen">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${VISITE_IMG})` }} data-testid="proposer-hero" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,5,5,0.62) 0%, rgba(5,5,5,0.78) 100%)" }} />
          <section className="relative z-10 min-h-screen flex items-center justify-center px-4 py-28 sm:py-32">
            <motion.div initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-4xl text-center text-white" data-testid="proposer-choice-cards">
              <p className="calli text-3xl sm:text-4xl text-[#C19A6B] leading-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.7)]">Vous êtes propriétaire ?</p>
              <p className="text-base sm:text-lg font-light text-zinc-100 mt-4 leading-relaxed max-w-2xl mx-auto drop-shadow-[0_1px_12px_rgba(0,0,0,0.85)]">
                Créez votre annonce test <strong className="font-semibold text-white">gratuitement</strong> et laissez-vous porter par la magie de <span className="text-[#C19A6B] font-semibold">OFF MARKETTE</span>. Nous vous mettrons en relation avec un <strong className="font-semibold text-white">agent spécialisé sur votre secteur</strong>, supervisé par <strong className="font-semibold text-white">Vincent Marette</strong>, cofondateur du site.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-9 text-left">
                <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} onClick={() => { setErr(""); setMode("online"); }} data-testid="proposer-choice-online" className="group text-left bg-white/[0.07] hover:bg-white/[0.12] backdrop-blur-sm p-7 transition-colors flex flex-col items-start gap-3 rounded-[10px] min-h-[240px]">
                  <div className="w-12 h-12 bg-[#C19A6B]/20 text-[#C19A6B] flex items-center justify-center rounded-md"><Globe className="w-6 h-6" /></div>
                  <h3 className="text-lg font-light text-white">Mon bien est déjà en ligne</h3>
                  <p className="text-sm text-zinc-300 font-light leading-relaxed">Déjà publié sur un site d'annonces ? Donnez-nous le lien : nous générons automatiquement votre fiche.</p>
                  <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#C19A6B] group-hover:gap-2.5 transition-all mt-auto">Générer mon annonce <ArrowRight className="w-4 h-4" /></span>
                </motion.button>
                <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} onClick={() => { setErr(""); setMode("offline"); }} data-testid="proposer-choice-offline" className="group text-left bg-white/[0.07] hover:bg-white/[0.12] backdrop-blur-sm p-7 transition-colors flex flex-col items-start gap-3 rounded-[10px] min-h-[240px]">
                  <div className="w-12 h-12 bg-white/10 text-white flex items-center justify-center rounded-md"><PenLine className="w-6 h-6" /></div>
                  <h3 className="text-lg font-light text-white">Mon bien est encore OFF MARKET</h3>
                  <p className="text-sm text-zinc-300 font-light leading-relaxed">Diffusé nulle part ? Décrivez-le via notre formulaire complet et ajoutez vos photos.</p>
                  <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-zinc-200 group-hover:text-[#C19A6B] group-hover:gap-2.5 transition-all mt-auto">Remplir le formulaire <ArrowRight className="w-4 h-4" /></span>
                </motion.button>
              </div>
            </motion.div>
          </section>
        </div>
        <Overlays />
      </PlatformLayout>
    );
  }

  // Formulaire simplifié « déjà en ligne »
  if (mode === "online") {
    return (
      <PlatformLayout solidHeader searchDock={false}>
        <div className="bg-zinc-100 text-[#050505] min-h-screen">
        <section className="pt-36 pb-8 max-w-2xl mx-auto px-6">
          <button onClick={() => { setErr(""); setMode(null); }} className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 hover:text-[#050505] mb-6" data-testid="proposer-online-back"><ArrowLeft className="w-4 h-4" /> Retour</button>
          <span className={groupTitle}>Mon bien est déjà en ligne</span>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight mt-2">Générer mon annonce</h1>
          <p className="text-sm sm:text-base font-light text-zinc-600 leading-relaxed mt-3">Indiquez le lien de votre annonce : nous récupérons automatiquement les informations et notre IA génère votre fiche. Créez votre compte en un clic pour la retrouver en prévisualisation privée dans votre espace, et la publier quand vous le souhaitez.</p>
        </section>
        {err && <div className="max-w-2xl mx-auto px-6 mb-4"><div className="bg-red-500/10 border border-red-500/30 text-red-600 text-sm px-4 py-3" data-testid="proposer-error">{err}</div></div>}
        <form onSubmit={submitOnline} className="max-w-2xl mx-auto px-6 pb-24 space-y-5" data-testid="proposer-online-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={lbl}>Nom *</label><input required value={on.owner_nom} onChange={(e) => setOnline("owner_nom", e.target.value)} className={inp} data-testid="po-nom" /></div>
            <div><label className={lbl}>Prénom</label><input value={on.owner_prenom} onChange={(e) => setOnline("owner_prenom", e.target.value)} className={inp} data-testid="po-prenom" /></div>
            <div><label className={lbl}>Téléphone *</label><input required value={on.owner_telephone} onChange={(e) => setOnline("owner_telephone", e.target.value)} className={inp} data-testid="po-tel" /></div>
            <div><label className={lbl}>E-mail</label><input type="email" value={on.owner_email} onChange={(e) => setOnline("owner_email", e.target.value)} className={inp} data-testid="po-email" /></div>
          </div>
          <div><label className={lbl}>URL de l'annonce existante *</label><input required value={on.url} onChange={(e) => setOnline("url", e.target.value)} className={inp} placeholder="https://www.exemple.fr/annonce/..." data-testid="po-url" /></div>
          {stagingPickerEl}
          <button type="submit" disabled={sending} className="flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-8 py-4 transition-colors disabled:opacity-60" data-testid="po-submit">
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Sparkles className="w-4 h-4" /> Générer mon annonce</>}
          </button>
        </form>
        </div>
        <Overlays />
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout solidHeader searchDock={false}>
      <div className="bg-zinc-100 text-[#050505] min-h-screen">
      {/* Intro */}
      <section className="pt-36 pb-10 max-w-4xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="space-y-5">
          <button onClick={() => { setErr(""); setMode(null); }} className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 hover:text-[#050505]" data-testid="proposer-offline-back"><ArrowLeft className="w-4 h-4" /> Retour</button>
          <span className={groupTitle}>Propriétaires et annonceurs</span>
          <h1 className="text-4xl sm:text-5xl font-light tracking-tight">Proposez votre bien</h1>
          <p className="text-base sm:text-lg font-light text-zinc-600 leading-relaxed">
            Vous souhaitez vendre votre bien ? Il n'est pas encore sur le marché ? Vous êtes au bon endroit. Soumettez-le en quelques instants : notre IA génère automatiquement une <strong className="font-semibold text-zinc-900">prévisualisation gratuite</strong> de votre annonce, <strong className="font-semibold text-zinc-900">avant toute publication</strong>. Vous bénéficiez de <strong className="font-semibold text-[#C19A6B]">crédits de prévisualisation offerts</strong> pour tester le rendu avant de la publier.
          </p>
          {pricing && (
            <div className="pt-6" data-testid="proposer-pricing">
              <span className="calli text-lg text-[#C19A6B]">Tarifs de publication</span>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                <div className="bg-gradient-to-b from-[#C19A6B]/10 to-white rounded-[5px] p-5 flex flex-col border-2 border-[#C19A6B] shadow-lg relative" data-testid="pricing-test">
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-wider bg-[#C19A6B] text-white px-2 py-0.5 rounded-full whitespace-nowrap">Offerte</span>
                  <div className="flex items-baseline justify-between mt-1">
                    <h3 className="text-lg font-semibold text-zinc-900">Annonce test</h3>
                  </div>
                  <div className="text-2xl font-light text-[#C19A6B] mt-1">Gratuit<span className="text-sm text-zinc-500"> / 1 annonce</span></div>
                  <p className="text-[11px] text-zinc-400 mb-3">1 crédit offert · valable une seule fois par utilisateur</p>
                  <ul className="space-y-1.5 text-sm text-zinc-600">
                    <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#C19A6B] shrink-0 mt-0.5" /><span><strong className="text-zinc-900">2 home stagings IA</strong> sur la photo principale</span></li>
                    <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#C19A6B] shrink-0 mt-0.5" /><span>Prévisualisation privée de votre annonce</span></li>
                    <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#C19A6B] shrink-0 mt-0.5" /><span>Sans engagement</span></li>
                  </ul>
                </div>
                {["classique", "pro", "premium"].map((k) => {
                  const t = pricing.tiers?.[k];
                  if (!t) return null;
                  const eur = Math.round(t.cost * pricing.price_per_credit_eur);
                  return (
                    <div key={k} className={`bg-white rounded-[5px] p-5 flex flex-col border transition-all hover:shadow-xl hover:shadow-black/5 ${k === "pro" ? "border-[#C19A6B] shadow-lg" : "border-zinc-200"}`} data-testid={`pricing-${k}`}>
                      <div className="flex items-baseline justify-between">
                        <h3 className="text-lg font-semibold text-zinc-900">{t.label}</h3>
                        {k === "pro" && <span className="text-[10px] uppercase tracking-wider bg-[#C19A6B] text-white px-2 py-0.5 rounded-full">Populaire</span>}
                      </div>
                      <div className="text-2xl font-light text-[#C19A6B] mt-1">{eur} €<span className="text-sm text-zinc-500"> / annonce</span></div>
                      <p className="text-[11px] text-zinc-400 mb-3">{t.cost} crédit{t.cost > 1 ? "s" : ""}</p>
                      <ul className="space-y-1.5 text-sm text-zinc-600">
                        {(t.features || []).map((ft, i) => <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#C19A6B] shrink-0 mt-0.5" /><span>{ft}</span></li>)}
                      </ul>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-zinc-500 mt-3">Prévisualisation <strong className="text-zinc-700">gratuite</strong> avant publication · {pricing.free_preview_limit} crédits de prévisualisation offerts à l'inscription.</p>
            </div>
          )}
        </motion.div>
      </section>

      {err && <div className="max-w-4xl mx-auto px-6 mb-4"><div className="bg-red-500/10 border border-red-500/30 text-red-600 text-sm px-4 py-3" data-testid="proposer-error">{err}</div></div>}

      <form onSubmit={submit} className="max-w-4xl mx-auto px-6 pb-24 space-y-12" data-testid="proposer-form">
        {/* Propriétaire */}
        <div className="space-y-5">
          <h2 className={groupTitle}>Vos informations</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={lbl}>Nom *</label><input required value={f.owner_nom} onChange={(e) => set("owner_nom", e.target.value)} className={inp} data-testid="p-nom" /></div>
            <div><label className={lbl}>Prénom</label><input value={f.owner_prenom} onChange={(e) => set("owner_prenom", e.target.value)} className={inp} data-testid="p-prenom" /></div>
            <div><label className={lbl}>Téléphone *</label><input required value={f.owner_telephone} onChange={(e) => set("owner_telephone", e.target.value)} className={inp} data-testid="p-tel" /></div>
            <div><label className={lbl}>E-mail</label><input type="email" value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} className={inp} data-testid="p-email" /></div>
          </div>
        </div>

        {/* Bien */}
        <div className="space-y-5">
          <h2 className={groupTitle}>Informations du bien</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={lbl}>Type de bien</label><select value={f.type} onChange={(e) => set("type", e.target.value)} className={inp} data-testid="p-type">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className={lbl}>Prix souhaité (€)</label><input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} className={inp} data-testid="p-prix" /></div>
            <div className="sm:col-span-2"><label className={lbl}>Adresse complète</label><input value={f.address} onChange={(e) => set("address", e.target.value)} className={inp} data-testid="p-adresse" /></div>
            <div><label className={lbl}>Ville</label>
              <CityAutocomplete
                value={f.city}
                onSelect={(c) => setF((p) => ({ ...p, city: c.city, postal_code: c.postal_code ?? p.postal_code, department: c.department ?? p.department, region: c.region ?? p.region }))}
                placeholder="Ex. Senlis"
                dark
                testId="p-ville"
                className={inp}
              />
            </div>
            <div><label className={lbl}>Code postal <span className="text-[#C19A6B]">(auto)</span></label><input value={f.postal_code} onChange={(e) => set("postal_code", e.target.value)} className={inp} data-testid="p-cp" placeholder="60300" /></div>
            <div><label className={lbl}>Surface habitable (m²)</label><input type="number" value={f.surface} onChange={(e) => set("surface", e.target.value)} className={inp} data-testid="p-surface" /></div>
            <div><label className={lbl}>Surface du terrain (m²)</label><input type="number" value={f.land} onChange={(e) => set("land", e.target.value)} className={inp} data-testid="p-terrain" /></div>
            <div><label className={lbl}>Nombre de pièces</label><input type="number" value={f.rooms} onChange={(e) => set("rooms", e.target.value)} className={inp} data-testid="p-pieces" /></div>
            <div><label className={lbl}>Nombre de chambres</label><input type="number" value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className={inp} data-testid="p-chambres" /></div>
          </div>
          <div><label className={lbl}>Description libre</label><textarea rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} className={inp} data-testid="p-desc" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={lbl}>Travaux éventuels</label><input value={f.travaux} onChange={(e) => set("travaux", e.target.value)} className={inp} data-testid="p-travaux" /></div>
            <div><label className={lbl}>Disponibilité</label><input value={f.disponibilite} onChange={(e) => set("disponibilite", e.target.value)} className={inp} placeholder="Ex : immédiate, sous 3 mois…" data-testid="p-dispo" /></div>
          </div>
        </div>

        {/* Médias */}
        <div className="space-y-5">
          <h2 className={groupTitle}>Photos & documents</h2>
          <div>
            <label className={lbl}>Photos du bien</label>
            <div className="flex flex-wrap gap-3">
              {f.images.map((src, i) => (
                <div key={i} className="relative w-28 h-20 group">
                  <img src={src} alt="" className="w-full h-full object-cover border border-white/10" />
                  <button type="button" onClick={() => set("images", f.images.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button type="button" onClick={() => photoRef.current.click()} className="w-28 h-20 border border-dashed border-zinc-300 flex items-center justify-center text-zinc-500 hover:border-[#C19A6B]/60" data-testid="p-upload-photos"><Upload className="w-5 h-5" /></button>
              <input ref={photoRef} type="file" accept="image/*" multiple onChange={uploadPhotos} className="hidden" />
            </div>
          </div>
          <div>
            <label className={lbl}>Documents (DPE, diagnostics, plans…)</label>
            <div className="space-y-2">
              {f.documents.map((d, i) => (
                <div key={i} className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 px-3 py-2 text-sm">
                  <FileText className="w-4 h-4 text-[#C19A6B]" /> <span className="flex-1 truncate">{d.name}</span>
                  <button type="button" onClick={() => set("documents", f.documents.filter((_, idx) => idx !== i))} className="text-zinc-500 hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <button type="button" onClick={() => docRef.current.click()} className="flex items-center gap-2 border border-dashed border-zinc-300 px-4 py-2.5 text-sm text-zinc-600 hover:border-[#C19A6B]/60" data-testid="p-upload-docs"><Upload className="w-4 h-4" /> Ajouter un document</button>
              <input ref={docRef} type="file" accept=".pdf,.doc,.docx,image/*" multiple onChange={uploadDocs} className="hidden" />
            </div>
          </div>
          {uploading && <p className="text-xs text-[#C19A6B] flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Upload en cours…</p>}
        </div>

        {/* Mises en scène IA + RGPD + submit */}
        <div className="space-y-5 border-t border-zinc-200 pt-8">
          {stagingPickerEl}
          <label className="flex items-start gap-3 text-sm text-zinc-600 cursor-pointer">
            <input type="checkbox" checked={f.consent} onChange={(e) => set("consent", e.target.checked)} className="w-4 h-4 mt-0.5 accent-[#C19A6B]" data-testid="p-consent" />
            <span>J'accepte que mes informations soient collectées et utilisées par OFF MARKETTE (Vincent Marette) dans le cadre de l'étude de mon bien, conformément à la politique de confidentialité (RGPD).</span>
          </label>
          <button type="submit" disabled={sending || uploading} className="flex items-center justify-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-8 py-4 transition-colors disabled:opacity-60" data-testid="p-submit">
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Envoi…</> : <><Send className="w-4 h-4" /> Soumettre mon bien</>}
          </button>
        </div>
      </form>
      </div>
      <Overlays />
    </PlatformLayout>
  );
}
